import { API_BASE_URL } from './apiConfig';

/**
 * Super-admin "browse as gym" (impersonation) — global fetch bridge.
 *
 * The useApi hook attaches X-Impersonate-Gym-Id so the backend re-maps the
 * request to the impersonated gym's owner. But many CRM pages (Plans, Services,
 * Purchases, Members, Products, Schedule, Attendance, Branches, Team, Classes…)
 * fetch with a raw supabase token instead of useApi, so they never send the
 * header. The backend then scopes them to the super admin's own (empty) gym
 * list and the pages render blank in preview.
 *
 * Rather than convert dozens of call sites, wrap window.fetch once so EVERY call
 * to our API carries the impersonation header when browsing is active. Mirrors
 * useApi exactly: never applied to /api/saas/* (those authenticate as the super
 * admin directly) and idempotent (never overrides a header useApi already set).
 */

// Must match ImpersonationContext's STORAGE_KEY.
const STORAGE_KEY = 'impersonation';

function readImpersonation(): { gymId: string; edit: boolean } | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.gymId) return null;
    return { gymId: parsed.gymId, edit: !!parsed.edit };
  } catch {
    return null;
  }
}

function urlOf(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return (input as Request).url;
}

export function installImpersonationFetch(): void {
  const w = window as unknown as { __impersonationFetchInstalled?: boolean };
  if (w.__impersonationFetchInstalled) return;
  w.__impersonationFetchInstalled = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    try {
      const impersonation = readImpersonation();
      if (impersonation) {
        const url = urlOf(input);
        if (
          url &&
          url.startsWith(API_BASE_URL) &&
          !url.startsWith(`${API_BASE_URL}/api/saas`)
        ) {
          const base =
            init?.headers ?? (input instanceof Request ? input.headers : undefined);
          const headers = new Headers(base as HeadersInit | undefined);
          if (!headers.has('X-Impersonate-Gym-Id')) {
            headers.set('X-Impersonate-Gym-Id', impersonation.gymId);
            if (impersonation.edit) headers.set('X-Impersonate-Edit', '1');
            return originalFetch(input, { ...init, headers });
          }
        }
      }
    } catch {
      /* fall through to the unmodified request */
    }
    return originalFetch(input, init);
  };
}
